import React from 'react';
import '../../styles/CallForParticipation.css';
import {
  Users, Landmark, Lightbulb, BarChart3, Handshake, Cog,
  Mic, MessageSquare, Presentation, Rocket, Calendar,
  FileText, Download, ArrowRight, Mail,
} from 'lucide-react';
import conferenceData from '../../data/callForParticipation';

/* One page, one stylesheet, one data file per event. The CANTO Annual
   Conference and CANTO Connect calls share this component — pass the event's
   data file as a prop; anything optional (key dates, the alternating accent)
   is driven by what that file contains. */

// Icon keys live in the data files so they stay free of JSX.
const ICONS = {
  executives: Users,
  policymakers: Landmark,
  technology: Lightbulb,
  investors: BarChart3,
  entrepreneurs: Handshake,
  innovators: Cog,
  rocket: Rocket,
  spotlight: Mic,
  keynote: Users,
  panelist: Users,
  dialogue: MessageSquare,
  workshop: Presentation,
  pitch: Rocket,
};

function Icon({ name }) {
  const Component = ICONS[name];
  return Component ? <Component /> : null;
}

export default function CallForParticipation({ data = conferenceData }) {
  const { contact } = data;
  // The Panama artwork alternates teal and red; the conference page is teal only.
  const accent = (i) => (data.alternateAccent && i % 2 === 1 ? ' cfp-accent-alt' : '');

  return (
    <div className='cfp-page'>
      {/* Hero — the supplied banner already carries the title, tagline and the
          submission dates, so it stands on its own with no text overlay. */}
      <div className='cfp-banner'>
        <img
          src={data.bannerUrl}
          alt={`${data.eyebrow} — ${data.title}: ${data.tagline}. Submissions open ${data.submissionsOpen}, close ${data.submissionsClose}.`}
        />
      </div>

      {/* Intro */}
      <div className='cfp-intro'>
        <div className='cfp-eyebrow'>{data.eyebrow}</div>
        <h1 className='cfp-intro-title'>{data.tagline}</h1>
        {data.intro.map((paragraph, i) => (
          <p className='cfp-intro-text' key={i}>{paragraph}</p>
        ))}
      </div>

      {/* PDF download */}
      <div className='cfp-download-wrapper'>
        <div className='cfp-download'>
          <div className='cfp-download-icon'>
            <FileText />
          </div>
          <div className='cfp-download-body'>
            <div className='cfp-download-label'>Ready to learn more?</div>
            <div className='cfp-download-title'>{data.pdfTitle}</div>
            <div className='cfp-download-text'>
              Discover the participation opportunities, priority themes, formats and submission requirements.
            </div>
          </div>
          <div className='cfp-download-action'>
            {data.pdfUrl ? (
              <a
                href={data.pdfUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='cfp-download-btn'
              >
                Download PDF
                <Download size={18} />
              </a>
            ) : (
              <span className='cfp-download-btn cfp-btn-pending' aria-disabled='true'>
                Download PDF
                <Download size={18} />
              </span>
            )}
            <div className='cfp-download-meta'>
              {data.pdfUrl ? `PDF • ${data.eyebrow}` : 'Document coming soon'}
            </div>
          </div>
        </div>
      </div>

      {/* Who should participate */}
      <div className='cfp-section'>
        <div className='cfp-section-title'>Who Should Participate?</div>
        <div className='cfp-audience-grid'>
          {data.audiences.map((audience) => (
            <div className='cfp-audience-card' key={audience.label}>
              <div className='cfp-audience-icon'><Icon name={audience.icon} /></div>
              <div className='cfp-audience-label'>{audience.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Twelve priority themes */}
      <div className='cfp-section'>
        <div className='cfp-section-title'>Twelve Priority Themes</div>
        <div className='cfp-themes-grid'>
          {data.themes.map((theme, i) => (
            <div className='cfp-theme-row' key={theme}>
              <span className={`cfp-theme-number${accent(i)}`}>{i + 1}</span>
              <span className='cfp-theme-label'>{theme}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Participation formats */}
      <div className='cfp-section'>
        <div className='cfp-section-title'>Participation Formats</div>
        <div className='cfp-formats-grid'>
          {data.formats.map((format) => (
            <div className='cfp-format-card' key={format.label}>
              <div className='cfp-format-icon'><Icon name={format.icon} /></div>
              <div className='cfp-format-label'>{format.label}</div>
              <div className='cfp-format-duration'>{format.duration}</div>
              <div className='cfp-format-desc'>{format.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Key dates */}
      {data.keyDates && (
        <div className='cfp-section'>
          <div className='cfp-section-title'>Key Dates</div>
          <div className='cfp-dates-grid'>
            {data.keyDates.map((entry, i) => (
              <div className='cfp-date-card' key={entry.label}>
                <div className={`cfp-date-icon${accent(i)}`}><Calendar /></div>
                <div>
                  <div className='cfp-date-date'>{entry.date}</div>
                  <div className='cfp-date-label'>{entry.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submit / contact footer band */}
      <div
        className='cfp-submit'
        style={{ backgroundImage: `url(${data.footerBgUrl})` }}
      >
        <div className='cfp-submit-inner'>
          <div className='cfp-submit-left'>
            <div className='cfp-submit-label'>Ready to Participate?</div>
            <div className='cfp-submit-title'>Submit Your Proposal</div>
            <div className='cfp-submit-sub'>Help shape the Caribbean's digital future.</div>
          </div>

          {data.formUrl ? (
            <a
              href={data.formUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='cfp-submit-btn'
            >
              {data.submitLabel}
              <ArrowRight size={18} />
            </a>
          ) : (
            <span className='cfp-submit-btn cfp-btn-pending' aria-disabled='true'>
              Opening Soon
            </span>
          )}

          <div className='cfp-submit-contact'>
            <div className='cfp-submit-contact-label'>For more questions:</div>
            <div className='cfp-submit-contact-name'>{contact.name}</div>
            <div className='cfp-submit-contact-role'>{contact.role}</div>
            <a className='cfp-submit-contact-email' href={`mailto:${contact.email}`}>
              <Mail size={14} />
              {contact.email}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
